/* Release times are confirmed by Shopify-rendered sections, never by a buyer's clock. */
if (!customElements.get('fuji-release')) {
  customElements.define('fuji-release', class FujiRelease extends HTMLElement {
    static requests = new Map();

    static async loadSection(url, signal) {
      const key = url.pathname + url.search;
      let entry = this.requests.get(key);
      if (!entry) {
        const controller = new AbortController();
        entry = { controller, clients: new Set(), settled: false };
        const timeout = setTimeout(() => controller.abort(), 12000);
        const requestUrl = new URL(url);
        requestUrl.searchParams.set('_fuji_release', String(Date.now()));
        entry.promise = fetch(requestUrl.pathname + requestUrl.search, { signal: controller.signal, cache: 'no-store' })
          .then(async response => {
            if (!response.ok) throw new Error('Release section unavailable');
            return { text: await response.text(), date: response.headers.get('Date') };
          }).finally(() => {
            entry.settled = true;
            clearTimeout(timeout);
            this.requests.delete(key);
          });
        this.requests.set(key, entry);
      }
      const release = () => {
        entry.clients.delete(signal);
        if (!entry.clients.size && !entry.settled) entry.controller.abort();
      };
      entry.clients.add(signal);
      signal.addEventListener('abort', release, { once: true });
      try { return await entry.promise; }
      finally { signal.removeEventListener('abort', release); release(); }
    }
    connectedCallback() {
      this.lifecycle = new AbortController();
      this.formatTimes();
      this.serverNow = Number(this.dataset.renderedAt) * 1000;
      this.measuredAt = performance.now();
      this.attempts = 0;
      const options = { signal: this.lifecycle.signal };
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.refresh();
      }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      if (this.dataset.releaseBlocked === 'true') this.refresh();
      else this.schedule();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
    }

    shouldRefresh() {
      const releaseAt = Number(this.dataset.releaseAt) * 1000;
      if (!(releaseAt > 0)) return false;
      if (this.dataset.releaseBlocked === 'true') return true;
      const elapsed = this.serverNow + performance.now() - this.measuredAt - releaseAt;
      // Inventory automation can finish after the release time. Keep the real sold-out
      // state until Shopify confirms stock, and stop watching after a bounded window.
      return this.dataset.releaseAvailable === 'false' && elapsed >= 0 && elapsed < 15 * 60 * 1000;
    }

    syncState(source) {
      for (const key of ['releaseBlocked', 'releaseAvailable', 'releaseAt', 'renderedAt']) {
        this.dataset[key] = source.dataset[key] || '';
      }
      const renderedAt = Number(source.dataset.renderedAt) * 1000;
      if (renderedAt > 0) {
        this.serverNow = renderedAt;
        this.measuredAt = performance.now();
      }
      this.schedule();
    }

    formatTimes() {
      this.querySelectorAll('[data-fuji-release-time]').forEach(time => {
        const date = new Date(time.dateTime);
        if (!Number.isFinite(date.getTime())) return;
        // The store operates on Japan time, regardless of the buyer's device zone.
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
        }).formatToParts(date);
        const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
        time.textContent = `${values.year}.${values.month}.${values.day} ${values.hour}:${values.minute} JST`;
      });
    }

    schedule(failed = false) {
      clearTimeout(this.timer);
      this.timer = undefined;
      if (!this.isConnected || !this.shouldRefresh()) return;
      const remaining = Number(this.dataset.releaseAt) * 1000 -
        (this.serverNow + performance.now() - this.measuredAt);
      // Refresh at the boundary, with a bounded retry for network errors/CDN lag.
      const delay = failed ? Math.min(60000, 5000 * 2 ** Math.min(this.attempts, 4)) :
        remaining > 0 ? Math.min(remaining + 250, 30000) : 5000;
      this.timer = setTimeout(() => this.refresh(), Math.max(250, delay));
    }

    cloneForContext(source) {
      const replacement = source.cloneNode(true);
      const info = this.closest('product-info');
      if (info?.dataset.originalSection && info.dataset.originalSection !== info.dataset.section) {
        const oldId = info.dataset.originalSection;
        const newId = info.dataset.section;
        replacement.innerHTML = replacement.innerHTML.replaceAll(oldId, newId);
        for (const attribute of Array.from(replacement.attributes)) {
          if (attribute.value.includes(oldId)) replacement.setAttribute(attribute.name, attribute.value.replaceAll(oldId, newId));
        }
      }
      return replacement;
    }

    replaceFrom(source) {
      const replacement = this.cloneForContext(source);
      this.replaceWith(replacement);
      window.Shopify?.PaymentButton?.init();
    }

    async refresh() {
      clearTimeout(this.timer);
      this.timer = undefined;
      if (!this.isConnected || this.pending || !this.shouldRefresh()) return;
      if (document.hidden) { this.schedule(); return; }
      const info = this.closest('product-info');
      if (info?.dataset.variantState === 'loading' || info?.querySelector('product-form[aria-busy="true"]') ||
          document.querySelector('cart-items[aria-busy="true"], cart-drawer-items[aria-busy="true"]')) {
        this.schedule(true); return;
      }
      const root = this.closest('[data-fuji-release-root]');
      if (!root || !this.dataset.releaseSection) return;
      const section = info?.dataset.originalSection || this.dataset.releaseSection;
      const url = new URL(this.dataset.releaseUrl || location.href, location.origin);
      if (url.origin !== location.origin) return;
      url.searchParams.set('section_id', section);
      const variant = info?.querySelector('product-form input[name="id"]')?.value;
      if (variant) url.searchParams.set('variant', variant);
      this.pending = true;
      const controller = new AbortController();
      this.request = controller;
      let failed = false;
      try {
        const response = await FujiRelease.loadSection(url, controller.signal);
        const html = new DOMParser().parseFromString(response.text, 'text/html');
        if (!this.isConnected || controller.signal.aborted) return;
        // A variant changed while this request was in flight: discard the old selection.
        if (info?.dataset.variantState === 'loading' ||
            (variant && variant !== info?.querySelector('product-form input[name="id"]')?.value)) return;
        const sourceKey = info?.dataset.originalSection
          ? root.dataset.fujiReleaseRoot.replace(info.dataset.section, info.dataset.originalSection)
          : root.dataset.fujiReleaseRoot;
        const source = Array.from(html.querySelectorAll('[data-fuji-release-root]'))
          .find(node => node.dataset.fujiReleaseRoot === sourceKey);
        if (!source) throw new Error('Release state missing');
        const clock = Date.parse(response.date || '');
        if (Number.isFinite(clock)) {
          this.serverNow = clock;
          this.measuredAt = performance.now();
        }
        const state = source.matches('fuji-release') ? source : source.querySelector('fuji-release');
        const blocked = state?.dataset.releaseBlocked === 'true';
        const stateChanged = blocked !== (this.dataset.releaseBlocked === 'true') ||
          (!blocked && state?.dataset.releaseAvailable !== this.dataset.releaseAvailable);
        if (stateChanged) {
          if (this.dataset.releaseScope === 'product') {
            const price = info?.querySelector(`[id="price-${info.dataset.section}"]`);
            const sourcePrice = html.getElementById(`price-${section}`);
            if (price && sourcePrice) price.innerHTML = sourcePrice.innerHTML;
            // Refresh size/color availability together with the buy button.
            info?.updateOptionValues?.(html);
            this.replaceFrom(source);
          } else {
            root.replaceWith(source.cloneNode(true));
          }
        } else if (state) {
          // The merchant may have postponed the release while this page was open.
          this.dataset.releaseAt = state?.dataset.releaseAt || '';
          this.dataset.releaseAvailable = state.dataset.releaseAvailable || '';
          const notice = this.querySelector('.fuji-release-notice');
          const sourceNotice = state?.querySelector('.fuji-release-notice');
          if (notice && sourceNotice) notice.innerHTML = sourceNotice.innerHTML;
          this.formatTimes();
        }
        this.attempts = 0;
      } catch (error) {
        if (this.isConnected) { failed = true; this.attempts += 1; }
      } finally {
        this.pending = false;
        if (this.isConnected && Number(this.dataset.releaseAt) > 0) this.schedule(failed);
      }
    }
  });
}
